import React, {useEffect, useRef, useState} from 'react';
import {useQueryClient} from "react-query";
import moment from "moment";
import __ from "lodash";
import {Grid, makeStyles} from "@material-ui/core";
import axios from "axios";

import {DatePicker, FormDialog, FormSelect, MaskedTime, Text, }from "../../../controls";
import GenerateTripValidator from "../validators/GenerateTripValidator.js";
import { useSchedulingScratchPads, useUpdateSchedulingScratchPad } from '../../../services/flightPlanning';
import EnquiryCostingSelector from './EnquiryCostingSelector';
import TripLegEditor from '../../Trips/components/TripListDialogs/TripLegEditor';
import TripsStyle from "../../Trips/styles/TripStyles";


const GenerateTripDialog = (_props) => {
  const props = {
    canEdit: false,
    ..._props,
  }
  const apiSitePrefix = props.apiSitePrefix
  const SystemData = props.SystemData;
  const classes = props.classes;
  const tripClasses = makeStyles(TripsStyle)();

  const eventBus = SystemData.eventBus;

  const minDate = moment().add(1, 'day').startOf('day').toDate();
  const maxDate = moment().add(6, 'month').startOf('day').toDate();
  const dateFormat = "DD/MM/YYYY";

  const [dialogStates, setDialogStates] = useState({
    showDialog: false,
    record: {},
    title: 'Generate Trip',
  });

  const flags = useRef({
    loadedScratchPads: false,
    loadedSectors: false
  });

  const queryClient = useQueryClient();

  const scratchPadExtraKeys = dialogStates.record?.scratchPadUser?[dialogStates.record?.scratchPadUser]:[]
  const scratchPads = useSchedulingScratchPads({queryClient, extraKeys: scratchPadExtraKeys, callback:()=> {
    flags.current.loadedScratchPads = true; 
  }});
  const handleUpdateSchedulingScratchPad = useUpdateSchedulingScratchPad();

  useEffect(()=> {
    const buildTrip = async (record) => {
      try {
        const trip = await axios.get(`${apiSitePrefix}/enquiries/buildtrip/${record.enquiryUuid}/${record.costingUuid}/${record.tripDate}/${record.initialTime}`);
        return trip;
      } catch (error) {
        if (!error?.response?.data) {
          SystemData.alert("Error", `Generating trip has failed. Server is not responding`);
          SystemData.toggleLoadingProgress(false);
        } else if (error?.response?.data?.errorMessage) {
          SystemData.alert("Error", `Generating trip has failed. ${error.response.data.errorMessage}`);
          SystemData.toggleLoadingProgress(false);
        } else {
          SystemData.alert("Error", `Generating trip has failed. An unexpected error has occurred.`);
          SystemData.toggleLoadingProgress(false);
        }
        return null;
      }
    }
    const open = async (record) => {
      const trip = await buildTrip(record);
      setDialogStates({...dialogStates, showDialog:true, record: {...record, ...trip?.data}});
    }
    eventBus.bind("GenerateTripDialog.open", open);
    return () => {
      eventBus.unbind("GenerateTripDialog.open", open);
    }
  }, [SystemData, apiSitePrefix, dialogStates, eventBus]);

  const scratchPadOptions = scratchPads.data?scratchPads.data.map((scratchPad, i) => {
    return {code: i, value: scratchPad.scratchPadName||`Scratch Pad ${i + 1}`};
  }):null;

  const handleLegsChanged = (changes, props) => {
    const {record, recordChange} = props;
    if (changes && changes.record) {
      const legs = __.sortBy(changes.record.legs, ['contractedDepartureTimeUTC']);
      record['legs'] = legs;
      recordChange({...record, legs});

      if (changes.resolve) {
        changes.resolve();
      }
    }
  }

  const handleSaveTrip = async (record) => {
    let deltas = {...scratchPads.data[record.scratchPad].deltas};
    const {
      legs,
      tripDate,
      uniqueCostingNo,
      scratchPad,
      scratchPadUser,
      enquiryUuid,
      costingUuid,
      tripUuid,
    } = record;

    try {
      for (let i = 0; i < legs.length; i++) {
        const {
          tripUuid,
          tripLegUuid: uuid,
          tripNo,
          originAirportCode,
          destinationAirportCode,
          alternateAirportCode,
          alternateAirportIcaoCode,
          transportType,
          airlineCode,
          flightNo,
          contractedDepartureTimeUTC: ctd,
          contractedArrivalTimeUTC: cta,
          scheduledDepartureTimeUTC: std,
          scheduledArrivalTimeUTC: sta,
          estimatedDepartureTimeUTC: etd,
          estimatedArrivalTimeUTC: eta,
          legType,
          aircraftId: aircraftReg,
          allowLoading,
          allowThroughCargo,
          outboundTurnaround,
          inboundTurnaround,
        } = legs[i];

        const legRecord = {
          tripUuid: tripUuid,
          tripPrefix: tripNo.substring(0, 3),
          tripNo: tripNo,
          tripDate,
          uniqueCostingNo,
          uuid,
          originAirportCode,
          destinationAirportCode,
          alternateAirportCode,
          alternateAirportIcaoCode,
          transportType,
          airlineCode,
          flightNo,
          scheduledDepartureTimeUtc: etd||std,
          scheduledArrivalTimeUtc: eta||sta,
          ctd,
          cta,
          std,
          sta,
          etd,
          eta,
          legType,
          aircraftReg,
          allowLoading,
          allowThroughCargo,
          changed: true,
          aircraftChanged: false,
          outboundTurnaround,
          inboundTurnaround,
          tripState: "SCHEDULED",
          new: true,
          type: "TRIP"
        }
        deltas.updated.trips.push(legRecord);
        deltas.hasChanges = true;
      }

      const oldData = scratchPads.data[scratchPad];
      const newData = {...scratchPads.data[scratchPad], deltas, enquiryUuid, costingUuid, tripUuid};
      // validateDeltas(deltas);

      const extraKeys = [scratchPadUser];
      handleUpdateSchedulingScratchPad.mutate({
        extraKeys, 
        record: newData, 
        oldRecord: oldData,
        postSuccess: () => {
          setDialogStates({...dialogStates, showDialog: false, record: null});
          queryClient.invalidateQueries(["SchedulingScratchPad"]);
        },
        postError: (data) => {
          if (data.errorMessage) {
            SystemData.alert("Error", `Saving trip to scratch pad has failed. ${data.errorMessage}`);
          } else {
            SystemData.alert("Error", `Saving trip to scratch pad has failed. An unexpected error has occurred.`);
          }
        }
      });
    } catch (err) {
      SystemData.alert("Error", `Saving trip to scratch pad has failed. An unexpected error has occurred.`);
      console.error(err);
    }
  }

  if (dialogStates.showDialog) {
    return (
      <FormDialog
        title={dialogStates.title}
        message="Please select the enquiry costing to generate a trip from"
        open={dialogStates.showDialog}
        record={__.cloneDeep(dialogStates.record)}
        dialogOptions={{width: "lg", maxWidth: "lg", fullWidth: true}}
        classes={classes}
        validator={GenerateTripValidator}
        validatorExtraData={{scratchPads: scratchPads?.data}}
        buttons = {{
          save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{
            if (status.buttonState && status.buttonState.disabled) return true;
            return status.error;
          }},
          cancel:{icon: "check_circle", key: "Cancel", label: "Close", color: "primary"}
        }}
        onChange={(state) => {
          if (state.buttonPressed === "save") {
            const record = state.record;
            handleSaveTrip(record);
          } else {
            setDialogStates({...dialogStates, showDialog: false, record: null});
          }
        }}

      >
        <Grid container checkChildren={true} spacing={3}>
          <Grid item checkChildren={true} xs={3}>
            {(record) =>(
              <EnquiryCostingSelector
                id="costingUuid"
                label="Costing"
                shrink={true}
                fullWidth={true}
                enquiryUuid={record.enquiryUuid} 
                returnType={"object"}
                onChange={async (costingObject, params) => {
                  if (costingObject && typeof costingObject === "object") {
                    let newRecord = __.cloneDeep(params.record);
                    newRecord['costingUuid'] = costingObject.value;
                    const trip = await axios.get(`${apiSitePrefix}/enquiries/buildtrip/${newRecord.enquiryUuid}/${newRecord.costingUuid}/${newRecord.tripDate}/${newRecord.initialTime}`);
                    newRecord = {...newRecord, ...trip?.data};
                    params.recordChange(newRecord);
                  }  
                }}
              />
            )}
          </Grid>
          <Grid item checkChildren={true} xs={2}>
            <div style={{paddingLeft: 0, display: "inline-block"}}>                
              <FormSelect 
                label="Scratch Pad"
                id="scratchPad"
                options={scratchPadOptions}
              />
            </div>
          </Grid>
          <Grid item checkChildren={true} xs={2}>
            <DatePicker
              id="tripDate"
              label="Trip Date"
              style={{width: "100%"}}
              minDate={minDate}
              maxDate={maxDate}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={(value, params) => {
                const {record, recordChange} = params;
                let newRecord = __.cloneDeep(record);
                const newTripDate = moment(value).startOf('day');
                if (!newTripDate.isValid()) return;
                const diffDays = newTripDate.diff(moment(record.tripDate).startOf('day'), 'days');
                newRecord['tripDate'] = newTripDate.toISOString();
                newRecord.legs.forEach((leg)=>{
                  leg.scheduledDepartureTimeUTC = moment(leg.scheduledDepartureTimeUTC).add(diffDays, 'days').toISOString();
                  leg.scheduledArrivalTimeUTC = moment(leg.scheduledArrivalTimeUTC).add(diffDays, 'days').toISOString();
                  leg.contractedDepartureTimeUTC = leg.scheduledDepartureTimeUTC;
                  leg.contractedArrivalTimeUTC = leg.scheduledArrivalTimeUTC;
                  leg.estimatedDepartureTimeUTC = leg.scheduledDepartureTimeUTC;
                  leg.estimatedArrivalTimeUTC = leg.scheduledArrivalTimeUTC;
                })
                recordChange(newRecord);
              }}
            />
          </Grid>
          <Grid item checkChildren={true} xs={1}>
            {(record, changeRecord) => (
              <MaskedTime
                id="initialTime"
                label="Starting"
                source="string"
                disabled={moment(record?.initialTime, "HH:mm").isValid() && (!Array.isArray(record?.legs) || record.legs.length < 1 || record?.initialTime !== moment(record.legs[0].scheduledDepartureTimeUTC).format("HH:mm"))}
                onChange={(value, params)=>{
                  // if (!value) return;
                  // if (typeof value !== 'string' || value.indexOf(':') < 1 || value.split(':').length !== 2) return;
                  value = value.replace(/\s/g, '0');
                  const [hours,  minutes] =  value.split(':');
                  if (isNaN(hours) || hours < 0 || hours > 23) return;
                  if (isNaN(minutes) || minutes < 0 || hours > 59) return;

                  const {record, recordChange} = params;
                  let newRecord = __.cloneDeep(record);

                  const oldTime = record.initialTime;
                  const [oldHours,  oldMinutes] =  oldTime.split(':');
                  
                  const diffMinutes = ((hours - oldHours) * 60) + (minutes - oldMinutes);

                  newRecord.legs.forEach((leg)=>{
                    leg.scheduledDepartureTimeUTC = moment(leg.scheduledDepartureTimeUTC).add(diffMinutes, 'minutes').toISOString();
                    leg.scheduledArrivalTimeUTC = moment(leg.scheduledArrivalTimeUTC).add(diffMinutes, 'minutes').toISOString();
                    leg.contractedDepartureTimeUTC = leg.scheduledDepartureTimeUTC;
                    leg.contractedArrivalTimeUTC = leg.scheduledArrivalTimeUTC;
                    leg.estimatedDepartureTimeUTC = leg.scheduledDepartureTimeUTC;
                    leg.estimatedArrivalTimeUTC = leg.scheduledArrivalTimeUTC;
                  })
                  newRecord['initialTime'] = `${hours}:${minutes}`;
                  recordChange(newRecord);
                }}
              />
            )}
          </Grid>
          <Grid item checkChildren={true} xs={2}>
            <Text label="Trip No." id="tripNo" readOnly={true} />
          </Grid>
          <Grid item checkChildren={true} xs={12}>
            {(record, changeRecord) => {
              return (
                <TripLegEditor 
                  tripRecord={record}
                  classes={tripClasses}
                  canEdit={true}
                  onChange={handleLegsChanged}
                  canChangeContractedTimes={true}
                  preventDelete={true}
                  hideTimes={true}
                />
              )
            }}
          </Grid>
        </Grid>
          
      </FormDialog>
    )
  }
  return (<></>);
}

export default GenerateTripDialog;
